import React, { useEffect, useState } from 'react'
import { Link } from 'gatsby'
import PropTypes from 'prop-types'
import { Flex, Button, Checkbox, Label, Spinner } from 'theme-ui'
import { useForm } from 'react-hook-form'
import { useTranslate } from '@chordcommerce/gatsby-theme-autonomy'
import { toURL } from '~/utils/intl/localePrefix'

import Text from '~/components/IntlComponents/Text'
import Input from '~/components/Generic/Form/Input'
import Select from '~/components/Generic/Form/Select'
import List from '~/components/Gift/List'

const Form = ({
  formSubmit,
  states,
  gifts,
  formSubmitting,
  multiUseError = '',
  isLoading,
}) => {
  const translate = useTranslate()
  const {
    register,
    handleSubmit,
    setValue,
    watch,
    clearError,
    formState: { errors },
  } = useForm()

  const selectedGift = watch('sku')

  const noSelectionNedeed = gifts.length === 1

  const [selectionMade, setSelectionMade] = useState(false)

  const giftSectionTitle = noSelectionNedeed
    ? translate('gifting_flow.selector.here_is_your_gift')
    : translate('gifting_flow.selector.choose_gift')

  const onGiftSelection = sku => {
    setValue('sku', sku)
    setSelectionMade(true)
    if (errors?.sku) clearError('sku')
  }

  useEffect(() => {
    if (noSelectionNedeed) {
      setValue('sku', gifts[0].sku)
      setSelectionMade(true)
    }
  }, [])

  const rowStyle = {
    width: '100%',
    justifyContent: 'space-between',
    flexFlow: ['column', 'nowrap'],
  }

  const fieldsetStyle = {
    border: 'none',
    width: '100%',
    maxWidth: 800,
    margin: '0 auto',
  }

  const legendtStyle = {
    display: 'block',
    textAlign: 'center',
    fontWeight: 'bold',
    fontFamily: 'heading',
    fontSize: 32,
    lineHeight: 1,
    color: 'secondary',
    textTransform: 'uppercase',
    margin: '10px auto 18px',
    maxWidth: [300, 700],
  }

  const subLegendStyle = {
    color: 'secondary',
    display: 'block',
    textAlign: 'center',
    fontSize: 14,
    margin: '0px auto 25px',
  }

  const fieldSpacing = {
    padding: '0 15px 35px',
  }

  const checkTextStyle = {
    color: 'secondary',
    fontSize: 14,
    fontFamily: 'heading',
  }
  const checkSubTextStyle = {
    color: 'tertiary',
    fontSize: 12,
    fontFamily: 'Sofia-Pro',
  }

  return (
    <Flex
      as="form"
      onSubmit={handleSubmit(formSubmit)}
      data-builder-component="form"
      data-name="gift-redemption-form"
      data-builder-model="gift"
      sx={{ flexDirection: 'column', margin: '0 auto 100px' }}
      data-testid="gift-form"
    >
      <List
        gifts={gifts}
        selected={selectedGift}
        selectionMade={selectionMade}
        onSelection={onGiftSelection}
        noSelectionNedeed={noSelectionNedeed}
        errorText={errors?.sku && translate('gifting_flow.selector.error')}
        title={giftSectionTitle}
        intro={translate('gifting_flow.selector.intro')}
      />

      <fieldset style={fieldsetStyle}>
        <Flex sx={rowStyle}>
          <Input
            {...register('firstname', {
              required: true,
              minLength: 2,
              maxLength: 80,
            })}
            label={translate('gifting_flow.form.firstname')}
            type="text"
            sx={fieldSpacing}
            errorText={
              errors?.firstname &&
              translate('gifting_flow.form.error.firstname')
            }
          />

          <Input
            {...register('lastname', {
              required: true,
              minLength: 2,
              maxLength: 80,
            })}
            label={translate('gifting_flow.form.lastname')}
            type="text"
            sx={fieldSpacing}
            errorText={
              errors?.lastname && translate('gifting_flow.form.error.lastname')
            }
          />
        </Flex>

        <Flex sx={rowStyle}>
          <Input
            {...register('email', {
              required: true,
              maxLength: 80,
              pattern:
                /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
            })}
            label={translate('gifting_flow.form.email')}
            type="email"
            sx={fieldSpacing}
            errorText={
              errors?.email && translate('gifting_flow.form.error.email')
            }
          />

          <Input
            {...register('phone', {
              required: false,
              minLength: 6,
              maxLength: 16,
            })}
            label={translate('gifting_flow.form.phone')}
            type="tel"
            sx={fieldSpacing}
            errorText={
              errors?.phone && translate('gifting_flow.form.error.phone')
            }
          />
        </Flex>
      </fieldset>

      <fieldset style={fieldsetStyle}>
        <legend style={{ width: '100%' }}>
          <Text sx={legendtStyle}>
            {translate('gifting_flow.form.delivery_info')}
          </Text>
          <Text sx={subLegendStyle}>
            {translate('gifting_flow.form.delivery_info_usa')}
          </Text>
        </legend>
        <Flex sx={rowStyle}>
          <Input
            {...register('address1', {
              required: true,
              minLength: 10,
              maxLength: 120,
            })}
            label={translate('gifting_flow.form.address1')}
            subLabel={translate('gifting_flow.form.addressWarn')}
            type="text"
            sx={fieldSpacing}
            errorText={
              errors?.address1 && translate('gifting_flow.form.error.address1')
            }
          />

          <Input
            {...register('city', {
              required: true,
              minLength: 2,
              maxLength: 80,
            })}
            label={translate('gifting_flow.form.city')}
            type="text"
            sx={fieldSpacing}
            errorText={
              errors?.city && translate('gifting_flow.form.error.city')
            }
          />
        </Flex>

        <Flex sx={rowStyle}>
          <Select
            sx={fieldSpacing}
            {...register('state_name', {
              required: true,
              validate: value => states.map(item => item.name).includes(value),
            })}
            label={translate('gifting_flow.form.state_name')}
            data-testid="state_name"
            errorText={
              errors?.state_name &&
              translate('gifting_flow.form.error.state_name')
            }
          >
            {states.map(({ id, name }) => (
              <option key={id} value={name}>
                {name}
              </option>
            ))}
          </Select>
          <Input
            {...register('zipcode', {
              required: true,
              minLength: 2,
              maxLength: 16,
            })}
            label={translate('gifting_flow.form.zipcode')}
            type="text"
            inputmode="numeric"
            pattern="^(?(^00000(|-0000))|(\d{5}(|-\d{4})))$"
            sx={fieldSpacing}
            errorText={
              errors?.zipcode && translate('gifting_flow.form.error.zipcode')
            }
          />
        </Flex>
        <Flex mb={50} pr={4} sx={{ columnGap: '8px' }}>
          <Label
            sx={{
              ...fieldSpacing,
              width: 'min-content',
            }}
            mr={1}
          >
            <Checkbox
              defaultChecked={false}
              {...register('subscribed', {
                required: false,
              })}
            />
          </Label>
          <Flex sx={{ flexDirection: 'column' }}>
            <Text sx={checkTextStyle} mb={2}>
              {translate('gifting_flow.form.checkbox_label')}
            </Text>
            <Text sx={checkSubTextStyle}>
              {translate('gifting_flow.form.checkbox_text', {
                terms: (
                  <Link to={toURL('/messaging-terms')}>
                    <Text sx={{ textDecoration: 'underline' }}>
                      {translate('gifting_flow.form.terms')}
                    </Text>
                  </Link>
                ),
                privacy_policy: (
                  <Link to={toURL('/messaging-privacy-policy')}>
                    <Text sx={{ textDecoration: 'underline' }}>
                      {translate('gifting_flow.form.privacy')}
                    </Text>
                  </Link>
                ),
              })}
            </Text>
          </Flex>
        </Flex>
      </fieldset>

      {multiUseError && (
        <Text
          sx={{
            m: '1rem auto',
            color: 'error',
            fontWeight: 'body',
            whiteSpace: 'pre-line',
            textAlign: 'center',
          }}
        >
          {multiUseError}{' '}
          <Text
            onClick={() => window?.GorgiasChat?.open()}
            sx={{ textDecoration: 'underline', cursor: 'pointer' }}
          >
            Contact us here for help
          </Text>
        </Text>
      )}

      <Button
        data-builder-component="button"
        data-name="gift-submit-button"
        m={'0 auto'}
        type="submit"
        variant="buttons.gift.form.inverted"
        disabled={formSubmitting || isLoading}
      >
        {isLoading ? (
          <Spinner color="black" size={24} />
        ) : (
          <Text>
            {formSubmitting
              ? translate('gifting_flow.form.submitting')
              : translate('gifting_flow.form.submit')}
          </Text>
        )}
      </Button>
    </Flex>
  )
}

Form.propTypes = {
  formSubmit: PropTypes.func,
  states: PropTypes.array,
}

export default Form
